import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import Layout from './Layout';
import SmartImage from './SmartImage';
import { formatCurrency } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';
import './SetTopBoxDemo.css';

interface SetTopBox {
  sku: string;
  name: string;
  model: string;
  display_name: string;
  price: number;
  available: number;
  minimum_order_quantity: number;
  image_path?: string;
  image_url?: string;
}

interface RelatedProduct {
  id: number;
  sku: string;
  name: string;
  description?: string;
  category: string;
  price: number;
  available: number;
  minimum_order_quantity: number;
  image_path?: string;
  image_url?: string;
  vendor?: string;
}

const SetTopBoxDemo: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [stbModels, setStbModels] = useState<SetTopBox[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [relatedProducts, setRelatedProducts] = useState<RelatedProduct[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch STB models on component mount
  useEffect(() => {
    fetchSTBModels();
  }, []);

  // Fetch related products when model is selected
  useEffect(() => {
    if (selectedModel) {
      fetchRelatedProducts(selectedModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModel]);

  const fetchSTBModels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/demo/set-top-boxes');
      setStbModels(response.data);
      // Select first model by default
      if (response.data.length > 0) {
        setSelectedModel(response.data[0].model);
      }
    } catch (err) {
      setError('Failed to load Set Top Box models');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (model: string) => {
    try {
      setLoading(true);
      const params: any = {};
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      const response = await api.get(`/demo/set-top-boxes/${model}/related-products`, { params });
      setRelatedProducts(response.data);
    } catch (err) {
      setError('Failed to load related products');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (product: RelatedProduct) => {
    try {
      await api.post('/cart/items', {
        product_id: product.id,
        quantity: product.minimum_order_quantity
      });
      showSuccess(`Added ${product.name} to cart!`);
    } catch (err) {
      showError('Failed to add to cart');
      console.error(err);
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/products/${productId}`);
  };

  // Get unique categories from related products and sort them in desired order
  const categoryOrder = ['Set Top Boxes', 'Software', 'Remote Controls', 'Power Supplies', 'Cables', 'Accessories'];
  const allCategories = Array.from(new Set(relatedProducts.map(p => p.category))).filter(Boolean);
  const categories = categoryOrder.filter(cat => allCategories.includes(cat))
    .concat(allCategories.filter(cat => !categoryOrder.includes(cat)));
  
  // Filter products based on selected category and sort by category order, then by price (expensive first)
  const getProducts = () => {
    let products = selectedCategory 
      ? relatedProducts.filter(p => p.category === selectedCategory)
      : relatedProducts;
    
    // Sort by category order first, then by price (highest first)
    return products.sort((a, b) => {
      const aCategoryIndex = categoryOrder.indexOf(a.category);
      const bCategoryIndex = categoryOrder.indexOf(b.category);
      
      // If categories are different, sort by category order
      if (aCategoryIndex !== bCategoryIndex) {
        const aIndex = aCategoryIndex === -1 ? 999 : aCategoryIndex;
        const bIndex = bCategoryIndex === -1 ? 999 : bCategoryIndex;
        return aIndex - bIndex;
      }
      
      // If same category, sort by price (highest first)
      return b.price - a.price;
    });
  };
  
  const filteredProducts = getProducts();

  return (
    <Layout>
      <div className="stb-demo-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Set Top Box Accessory Finder</h1>
            <p className="lead">Select your hotel's Set Top Box model to view compatible accessories and replacement parts</p>
          </div>
        </div>
      </div>
      
      <div className="container stb-demo-content">
      
      {/* Info Box */}
      <div style={{
        background: '#e3f2fd',
        border: '1px solid #90caf9',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '3rem',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <i className="fas fa-info-circle" style={{ fontSize: '24px', color: '#1976d2' }}></i>
        <div>
          <strong style={{ color: '#1976d2' }}>How it works:</strong>
          <p style={{ margin: '4px 0 0 0', color: '#424242' }}>
            This tool helps you find the right accessories for your existing Set Top Box. Simply select your model below to see all compatible cables, remotes, power supplies, and other accessories.
          </p>
        </div>
      </div>

      {/* STB Model Selection */}
      <div className="stb-model-selection">
        <h2 className="section-title">Select Your Set Top Box Model</h2>
        <p className="section-subtitle">Choose the model currently installed at your site</p>
        <div className="stb-models-grid">
          {stbModels.map((stb) => (
            <div 
              key={stb.sku} 
              className={`stb-model-card ${selectedModel === stb.model ? 'selected' : ''}`}
              onClick={() => setSelectedModel(stb.model)}
            >
              <div className="stb-model-image">
                <SmartImage
                  src={stb.image_url || (stb.image_path ? `/images/product/${stb.image_path.split('/').pop()}` : undefined)}
                  alt={stb.name}
                  className="model-image"
                  productName={stb.name}
                  productCategory="Set Top Box"
                  productSku={stb.sku}
                />
              </div>
              <div className="stb-model-info">
                <h3>{stb.display_name}</h3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      {selectedModel && categories.length > 0 && (
        <div className="accessories-section">
          <div className="section-header">
            <div>
              <h2 className="section-title">Compatible Accessories for {stbModels.find(s => s.model === selectedModel)?.display_name || selectedModel}</h2>
              <p className="section-subtitle">These accessories and parts are guaranteed to work with your Set Top Box</p>
            </div>
            <div className="category-pills">
              <button 
                className={`category-pill ${selectedCategory === '' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('')}
              >
                All Categories
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className={`category-pill ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Related Products */}
      {selectedModel && (
        <div className="related-products-section">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i> {error}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="alert alert-info" role="alert">
              <i className="fas fa-info-circle me-2"></i> {selectedCategory ? `No products found in ${selectedCategory} category.` : 'No related products found for this model.'}
            </div>
          ) : (
            <div className="accessories-grid">
              {filteredProducts.map((product) => (
                <div 
                  key={product.sku} 
                  className="accessory-card"
                  onClick={() => handleProductClick(product.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="accessory-image">
                    <SmartImage
                      src={product.image_url || (product.image_path ? `/images/product/${product.image_path.split('/').pop()}` : undefined)}
                      alt={product.name}
                      className="product-image"
                      productName={product.name}
                      productCategory={product.category}
                      productSku={product.sku}
                    />
                  </div>
                  <div className="accessory-details">
                    <h4>{product.name}</h4>
                    <p className="sku">SKU: {product.sku}</p>
                    {product.description && (
                      <p className="description">{product.description}</p>
                    )}
                    <div className="accessory-meta">
                      <span className="category-tag">{product.category}</span>
      
                    </div>
                    <div className="accessory-footer">
                      <div className="price-info">
                        <span className="price">{formatCurrency(product.price || 0)}</span>
                        {product.minimum_order_quantity > 1 && (
                          <span className="min-qty">Min: {product.minimum_order_quantity}</span>
                        )}
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                        className="add-to-cart-btn"
                      >
                        <i className="fas fa-cart-plus"></i> Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>
    </Layout>
  );
};

export default SetTopBoxDemo;